import { API_URL } from '../configs/apiUrls';

const isOfficeCssFragment = (chunk) =>
	(/\bmso-[\w-]+\s*:/i.test(chunk) || /\.xl\d+\s*\{/i.test(chunk)) &&
	/\{[\s\S]*\}/.test(chunk);

/** Matches Excel/Word CSS rules left as visible text after <style> tags are dropped. */
const OFFICE_CSS_RULE_RUN =
	/(?:(?:@media[^{]*)?\s*(?:table|tr|td|th|col|colgroup|thead|tbody|\.xl\d+|\*)\s*\{[^}]*\}\s*)+/gi;

/**
 * Strip MS Office / Excel paste artifacts. CKEditor cannot render <style> tags,
 * so those rules otherwise appear as visible CSS text in the editor and in
 * appraisal description views that render stored HTML.
 */
export const sanitizeCkEditorHtml = (html) => {
	if (!html || typeof html !== 'string') return '';

	let result = html
		.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
		.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
		.replace(/<!--\[if[\s\S]*?<!\[endif\]>/gi, '')
		.replace(/<\?xml[\s\S]*?\?>/gi, '')
		.replace(/<meta\b[^>]*\/?>/gi, '')
		.replace(/<link\b[^>]*\/?>/gi, '')
		.replace(/<\/?(?:html|head|body)(?:\s[^>]*)?>/gi, '');

	result = result.replace(
		/<(?:p|div|span)(?:\s[^>]*)?>[\s\S]*?<\/(?:p|div|span)>/gi,
		(match) => (isOfficeCssFragment(match) ? '' : match)
	);

	result = result.replace(OFFICE_CSS_RULE_RUN, (match) =>
		isOfficeCssFragment(match) ? '' : match
	);

	return result.trim();
};

export const setupCkEditorSanitization = (editor) => {
	if (!editor || editor._ckSanitizationInstalled) return;
	editor._ckSanitizationInstalled = true;

	editor.editing.view.document.on(
		'clipboardInput',
		(evt, data) => {
			const html = data.dataTransfer.getData('text/html');
			if (html) {
				data.dataTransfer.setData('text/html', sanitizeCkEditorHtml(html));
			}
		},
		{ priority: 'high' }
	);
};

export const CK_CONFIGS = (token = localStorage.getItem('token')) => ({
	toolbar: {
		items: [
			'heading',
			
			'bold',
			'underline',
			'italic',
			'highlight',
			'blockQuote',
			'bulletedList',
			'numberedList',
			
			'outdent',
			'indent',
			
			'alignment',
			'fontSize',
			'fontColor',
			'fontBackgroundColor',
			'fontFamily',
			'insertTable',
			
			'code',
			'codeBlock',
			'htmlEmbed',
			'specialCharacters',
			'horizontalLine',
			
			'undo',
			'redo'
		]
	},
	language: 'en',
	ckfinder: {
		// Upload the images to the server using the CKFinder QuickUpload command.
		uploadUrl: API_URL + 'common/uploadCKMedia?token=' + token
	},
	image: {
		// Configure the available styles.
		styles: ['alignLeft', 'alignCenter', 'alignRight', 'full', 'side'],
		// Configure the available image resize options.
		resizeOptions: [
			{
				name: 'resizeImage:original',
				label: 'Original',
				value: null
			},
			{
				name: 'resizeImage:25',
				label: '25%',
				value: '25'
			},
			{
				name: 'resizeImage:50',
				label: '50%',
				value: '50'
			},
			{
				name: 'resizeImage:75',
				label: '75%',
				value: '75'
			}
		],
		// You need to configure the image toolbar, too, so it shows the new style
		// buttons as well as the resize buttons.
		toolbar: [
			'imageStyle:alignLeft',
			'imageStyle:alignCenter',
			'imageStyle:alignRight',
			'imageStyle:full',
			'imageStyle:side',
			'|',
			'resizeImage',
			'|',
			'imageTextAlternative'
		]
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableCellProperties',
			'tableProperties'
		]
	}
});
